'use client';

import React from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';
import * as HeroIconsSolid from '@heroicons/react/24/solid';

type IconVariant = 'outline' | 'solid';

/**
 * Heroicon by name.
 *
 * `name` is a plain string rather than a union of the ~300 icon names,
 * because most call sites pick it from a config array where a literal union
 * would force a cast at every use. The trade is that a typo cannot be caught
 * at compile time -- so an unknown name renders a visible question mark and
 * logs, instead of crashing or silently rendering nothing.
 */
type HeroIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement> & { title?: string }>;

interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'onClick'> {
  name: string;
  variant?: IconVariant;
  size?: number;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export default function Icon({
  name,
  variant = 'outline',
  size = 24,
  className = '',
  onClick,
  disabled = false,
  ...props
}: IconProps) {
  const iconSet: Record<string, HeroIconComponent> =
    variant === 'solid'
      ? (HeroIconsSolid as unknown as Record<string, HeroIconComponent>)
      : (HeroIcons as unknown as Record<string, HeroIconComponent>);

  const IconComponent = iconSet[name] ?? HeroIcons.QuestionMarkCircleIcon;

  if (!iconSet[name] && process.env.NODE_ENV !== 'production') {
    console.warn(`Icon: no ${variant} heroicon named "${name}"`);
  }

  const interaction = disabled
    ? 'opacity-50 cursor-not-allowed'
    : onClick
      ? 'cursor-pointer hover:opacity-80'
      : '';

  return (
    <IconComponent
      width={size}
      height={size}
      aria-hidden={props['aria-label'] ? undefined : true}
      className={`${interaction} ${className}`}
      onClick={disabled ? undefined : onClick}
      {...props}
    />
  );
}
